import usePureState from '@/hooks/usePureState';
import { getToday } from '@/services/time/time.service';
import { useBreakpointValue } from '@chakra-ui/react';
import React, { useLayoutEffect, useRef } from 'react';
import { Temporal } from 'temporal-polyfill';
import { ICalendarEventMinimumData } from './types/ICalendarEvent';
import { ITimeZone } from './types/ITimeZone';
import weekCalendarService from './week-calendar.service';

interface IProps<TEvent extends ICalendarEventMinimumData> {
	/**
	 * Controls the size in pixels of an hour.
	 * @default 60
	 */
	hourHeightInPixels: number;
	/**
	 * TimeZone used by default in the calendar.
	 */
	timeZone: ITimeZone;
	/**
	 * First day to show in the calendar.
	 * By default 7 days will be displayed
	 */
	from: Temporal.PlainDate;
	defaultActiveDay: Temporal.PlainDate;
	/**
	 * Array of calendar events
	 */
	events: Array<TEvent>;
	/**
	 * Days to display in the calendar.
	 *
	 * @default 7
	 */
	visibleDays?: number;
	/**
	 *
	 */
	onRangeChanged?: (args: { start: Temporal.PlainDate; end: Temporal.PlainDate }) => void;
	/**
	 *
	 */
	onDayClick?: (value: Temporal.PlainDate) => void;
	/**
	 * The vertical scroll offset in pixels
	 */
	verticalScroll?: number;
	/**
	 *
	 */
	onVerticalScroll?: (args: { scrollTop: number }) => void;
	/**
	 *
	 * @param event
	 * @returns
	 */
	onEventUpdated: (event: TEvent) => void;
}

interface IState {
	from: Temporal.PlainDate;
	currentDay: Temporal.PlainDate;
	ignoreScrollEvents: boolean;
	headerClickTimeout: NodeJS.Timeout | null;
	/**
	 * Displays a time in the left bar.
	 */
	highlightTime?: Temporal.PlainTime;
}

const DAY_SCROLL_DURATION_MS = 700;

export function useWeekCalendar<TEvent extends ICalendarEventMinimumData>(props: IProps<TEvent>) {
	const isMobile = useBreakpointValue({ base: true, lg: false });
	// TODO: Do we really need this state? should this be injected via props ?
	const { state, dispatch } = usePureState<IState>({
		from: props.from,
		currentDay: props.defaultActiveDay,
		ignoreScrollEvents: false,
		headerClickTimeout: null,
	});

	const visibleDays = isMobile ? 21 : 7;

	const days = weekCalendarService.getWeekDays(state.from.subtract({ days: isMobile ? 7 : 0 }), isMobile ? 21 : 7);
	const calendarScrollRef = useRef<HTMLDivElement>(null);
	const calendarBottomRef = useRef<HTMLDivElement>(null);
	const assignedEvents = weekCalendarService.assignEventToPlainDates(props.events);
	const today = getToday();

	useLayoutEffect(
		function adjustScrollLeft() {
			if (!calendarScrollRef.current) {
				return;
			}

			const left = (state.from.until(state.currentDay).days + 7) * calendarScrollRef.current.offsetWidth;

			if (typeof calendarScrollRef.current.scrollTo === 'function') {
				calendarScrollRef.current.scrollTo({ left });
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	useLayoutEffect(
		function adjustScrollTop() {
			console.count('adjustScrollTop');

			if (!calendarBottomRef.current) {
				return;
			}

			// On test environment scrollTo is not defined so we need to check.
			if (typeof calendarBottomRef.current.scrollTo === 'function') {
				calendarBottomRef.current.scrollTo({ top: props.verticalScroll, behavior: 'instant' });
			}
		},
		[props.verticalScroll]
	);

	function handleWeekChange(from: Temporal.PlainDate) {
		dispatch({ from });

		if (typeof props.onRangeChanged === 'function') {
			return props.onRangeChanged({
				start: from,
				end: from.add({ days: visibleDays }),
			});
		}
	}

	function handleHeaderDayClick(value: Temporal.PlainDate, dayIndex: number) {
		if (!calendarScrollRef.current) {
			return;
		}

		if (props.onDayClick) {
			props.onDayClick(value);
		}

		dispatch({ currentDay: value, ignoreScrollEvents: true });

		const scrollPosition = dayIndex * calendarScrollRef.current.offsetWidth;

		calendarScrollRef.current.scrollTo({ left: scrollPosition, behavior: 'smooth' });

		if (state.headerClickTimeout) {
			clearTimeout(state.headerClickTimeout);
		}

		const timeout = setTimeout(() => {
			dispatch({ currentDay: value, ignoreScrollEvents: false });
		}, DAY_SCROLL_DURATION_MS);

		dispatch({ headerClickTimeout: timeout });
	}

	/**
	 * Controls the day scroll (horizontal)
	 */
	function handleScroll(e: React.MouseEvent<HTMLDivElement>) {
		if (state.ignoreScrollEvents) {
			return;
		}

		const scrollLeft = e.currentTarget.scrollLeft;
		const dayWidth = e.currentTarget.offsetWidth;

		const diffInDays = Math.floor((scrollLeft + dayWidth / 2) / dayWidth) - 7;

		if (diffInDays >= 7) {
			handleWeekChange(state.from.add({ days: 7 }));
		}

		if (diffInDays === -1) {
			handleWeekChange(state.from.add({ days: -1 }));
		}

		dispatch({ currentDay: state.from.add({ days: diffInDays }) });
	}

	/**
	 * Controls the hourly scroll.
	 */
	function handleVerticalScroll(e: React.UIEvent<HTMLDivElement, UIEvent> | React.MouseEvent<HTMLDivElement>) {
		if (props.onVerticalScroll) {
			props.onVerticalScroll({ scrollTop: e.currentTarget.scrollTop });
		}
	}

	function handleTodayClick() {}

	function handleEventPartialUpdated(event: TEvent) {
		const originalEvent = props.events.find((e) => e.id === event.id)!;
		// Start changed
		if (!originalEvent.start.toPlainTime().equals(event.start.toPlainTime())) {
			const time = event.start.toPlainTime();
			dispatch({ highlightTime: time });
			return;
		}
		// End changed
		const time = event.end.toPlainTime();
		dispatch({ highlightTime: time });
	}

	function handleEventUpdated(event: TEvent) {
		dispatch({ highlightTime: undefined });
		props.onEventUpdated(event);
	}

	return {
		assignedEvents,
		calendarBottomRef,
		calendarScrollRef,
		days,
		state,
		today,
		visibleDays,
		from: Temporal.PlainDate.from(state.from),
		to: Temporal.PlainDate.from(state.from).add({ days: 7 }),
		handleDayClick: handleHeaderDayClick,
		handleScroll,
		handleTodayClick,
		handleVerticalScroll,
		handleWeekChange,
		handleEventPartialUpdated,
		handleEventUpdated,
	};
}
